import { processFeatureFlags } from './processFeatureFlags.ts';
import type {
	SiteInformationQuery,
	Environment,
	ConfluenceEdition,
	AccessStatus,
} from './__types__/SiteInformationQuery.ts';
import type { SessionDataType } from './SessionDataTypes.ts';

export const getEmptySessionData = (): SessionDataType => processSessionData(null);

export function processSessionData(data: SiteInformationQuery | null): SessionDataType {
	const { originalFeatureFlags, featureFlagsWithOverrides, featureFlagClient } =
		processFeatureFlags([], []);

	// nullability of `tenant` is probably a schema issue
	// `userForAnalytics.id` would probably never be null, follow up with Xiao
	const confluenceLocale = data?.user?.confluence?.locale?.replace('_', '-');
	return {
		shard: data?.tenant?.shard || '', // `tenant` nullability is a problem in cc-graphql schema
		cloudId: data?.tenant?.cloudId || '', // `tenant` nullability is a problem in cc-graphql schema
		activationId: data?.tenant?.activationId || '',
		orgId: data?.organization?.orgId || undefined,
		environment: (data?.tenant?.environment as Environment) || 'PRODUCTION', // `tenant` nullability is a problem in cc-graphql schema
		edition: (data?.confluence_tenantContext?.editions?.confluence as ConfluenceEdition) || null,
		entitlementId:
			data?.confluence_tenantContext?.licenseStates?.confluence?.ccpEntitlementId || null,
		userId: data?.user?.id || null,
		displayName: data?.user?.displayName || null,
		isLoggedIn: Boolean(data?.user?.id),
		hasConfluenceUser: Boolean(data?.user?.confluence),
		// CCP-472: This is for JSD users who are unlicensed for
		// Confluence usage. In that case, we need to skip
		// `WatchDialogQuery` as they are causing NullPointerExceptions
		// in the BE.
		isLicensed: Boolean(data?.user?.confluence?.accountId && data?.user?.confluence?.userKey),
		accessStatus: (data?.user?.confluence?.accessStatus as AccessStatus) || null,
		userIdForAnalytics: data?.userForAnalytics?.id || null,
		featureFlags: featureFlagsWithOverrides,
		featureFlagClient,
		_originalFeatureFlags: originalFeatureFlags,
		locale: confluenceLocale || 'en-US',
		timeZone: data?.user?.timeZone || null,
		tenantTimeZone: data?.confluence_tenantContext?.timeZone || null,
		tenantBaseUrl: data?.confluence_tenantContext?.baseUrl || null,
		isAdminHubAIEnabled: Boolean(data?.getAIConfig?.isEnabled),
		isRovoEnabled: Boolean(data?.getAIConfig?.isRovoEnabled),
		isRovoLLMEnabled: Boolean(data?.getAIConfig?.isRovoLLMEnabled),
		isAIEarlyAccessEnabled: Boolean(data?.getAIConfig?.isAIEarlyAccessEnabled),
		isAtlassianHostedLLMOnlyEnabled: Boolean(data?.getAIConfig?.isAtlassianHostedLLMOnlyEnabled),
		monolithRegion: data?.confluence_tenantContext?.monolithRegion || null,
		isHideTitleInUrl: Boolean(data?.siteSettings?.isHideTitleInUrl),
	};
}
